/**
 * FootingsPage — unified footing-design entry with a type-picker row.
 *
 * Layout:
 *   - Top header bar:        title only ("Diseño de zapatas").
 *   - Type-picker band:      4 large tabs with breathing room.
 *   - Sub-page render area:  the active sub-designer with hideHeader=true.
 *     Each sub-page renders its own DesignToolbar (status + PDF/XLSX +
 *     Save) so users get consistent controls regardless of the type.
 *
 *   /app/footings/new
 *   /app/footings/:id   (sub-page hydrates from the saved design)
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FootingDesignPage } from './FootingDesignPage';
import { StripFootingPage } from './StripFootingPage';
import { CombinedFootingPage } from './CombinedFootingPage';
import { ConnectedFootingPage } from './ConnectedFootingPage';
import { useDesign } from '@/features/designs/useDesigns';

type FootingType = 'isolated' | 'strip' | 'combined' | 'connected';

interface TypeInfo {
  id: FootingType;
  label: string;
  short: string;
  description: string;
}

const TYPES: TypeInfo[] = [
  {
    id: 'isolated',
    label: 'Aislada',
    short: 'Una columna',
    description:
      'Soporta una sola columna centrada. Diseño biaxial: presión del suelo, cortante 1D en cada dirección, punzonado a d/2 y flexión a la cara.',
  },
  {
    id: 'strip',
    label: 'Corrida',
    short: 'Bajo muro',
    description:
      'Franja continua bajo un muro. Diseño por metro lineal: presión, cortante 1D, flexión perpendicular y acero de repartición longitudinal.',
  },
  {
    id: 'combined',
    label: 'Combinada',
    short: 'Dos columnas',
    description:
      'Una zapata bajo dos columnas próximas. El centroide de cargas se alinea con el centro de la zapata para presión uniforme; diseño como viga ancha en planta.',
  },
  {
    id: 'connected',
    label: 'Conectada',
    short: 'Medianera + interior',
    description:
      'Dos zapatas independientes unidas por una viga de cimentación que toma la excentricidad de la columna medianera. Se diseñan ambas zapatas y la viga.',
  },
];

export function FootingsPage() {
  const [type, setType] = useState<FootingType>('isolated');
  const typeInfo = TYPES.find((t) => t.id === type)!;

  // URL hydration: /app/footings/:id loads the design and auto-selects the
  // tab that matches its subtype, so the sub-page can read the same URL
  // and pre-fill its inputs.
  const params = useParams<{ id?: string }>();
  const designId =
    params.id && params.id !== 'new' && /^\d+$/.test(params.id) ? Number(params.id) : null;
  const designQuery = useDesign(designId);

  useEffect(() => {
    const d = designQuery.data;
    if (!d) return;
    const valid: FootingType[] = ['isolated', 'strip', 'combined', 'connected'];
    if (d.subtype && valid.includes(d.subtype as FootingType)) {
      setType(d.subtype as FootingType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designQuery.data?.id]);

  return (
    <div className="flex h-full flex-col">
      {/* Slim header — just title, leaves room for tabs below. */}
      <header
        className="flex h-14 shrink-0 items-center border-b px-6"
        style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}
      >
        <div>
          <div
            className="text-[10px] uppercase tracking-[0.18em]"
            style={{ color: 'var(--text-muted)' }}
          >
            Software E.060 + E.050
          </div>
          <div
            className="text-sm leading-none"
            style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--text-primary)' }}
          >
            Diseño de zapatas
          </div>
        </div>
      </header>

      {/* Dedicated band for the type picker — clearly separated from the
          page title and the toolbar below. Gives the tabs visual breathing
          room (the previous all-in-one header crammed them against the top). */}
      <div
        className="flex flex-wrap items-center gap-4 border-b px-6 py-3"
        style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}
      >
        <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
          Tipo de zapata
        </div>
        <div
          className="flex overflow-hidden rounded-[var(--radius-sm)] border"
          style={{ borderColor: 'var(--border-default)' }}
        >
          {TYPES.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setType(t.id)}
              className="h-9 px-4 text-[12px] font-semibold tracking-[0.06em] transition-colors"
              style={{
                background: type === t.id ? 'var(--accent)' : 'transparent',
                color: type === t.id ? 'var(--surface-base)' : 'var(--text-secondary)',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="hidden flex-1 text-[11.5px] lg:block" style={{ color: 'var(--text-muted)' }}>
          <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{typeInfo.short}</span>
          {' · '}
          {typeInfo.description}
        </div>
      </div>

      {/* Body — render the active designer with its own header hidden.
          Each sub-page mounts its own DesignToolbar in `hideHeader` mode. */}
      <div className="flex-1 min-h-0">
        {type === 'isolated' && <FootingDesignPage hideHeader />}
        {type === 'strip' && <StripFootingPage hideHeader />}
        {type === 'combined' && <CombinedFootingPage hideHeader />}
        {type === 'connected' && <ConnectedFootingPage hideHeader />}
      </div>
    </div>
  );
}
